import React, { useState, useMemo } from 'react'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Stack
} from '@mui/material'
import Grid from '@mui/material/Grid'
import {
  Add as AddIcon,
  Science as ScienceIcon
} from '@mui/icons-material'
import Layout from '../components/Layout/Layout'
import { usePool } from '../contexts/PoolContext'
import { useMeasurements } from '../hooks/useMeasurements'
import { useChemicalAdditions } from '../hooks/useChemicalAdditions'
import MeasurementForm from '../components/Measurements/MeasurementForm'
import PoolList from '../components/Pool/PoolList'
import PoolSelector from '../components/Pool/PoolSelector'
import UnifiedTimeline from '../components/Common/UnifiedTimeline'
import ParameterCard from '../components/Common/ParameterCard'
import TestReminder from '../components/Common/TestReminder'
import { Link } from 'react-router-dom'
import usePageTracking from '../hooks/usePageTracking'
import { buildParameterSummaries } from '../utils/trendUtils'
import { calculateTargetRanges, getParameterStatus, getStatusColor, getStatusText } from '../utils/chemicalCalculations'

const Dashboard = () => {
  usePageTracking('Dashboard');
  
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  
  const { selectedPool, setSelectedPool, pools } = usePool()
  const {
    createMeasurement,
    updateMeasurement,
    deleteMeasurement,
    getRecentMeasurements,
    getLatestMeasurement
  } = useMeasurements(selectedPool?.id)
  const { 
    deleteChemicalAddition, 
    getRecentAdditions 
  } = useChemicalAdditions(selectedPool?.id)
  
  const [showMeasurementForm, setShowMeasurementForm] = useState(false)
  const [editingMeasurement, setEditingMeasurement] = useState(null)
  const [reminderDismissed, setReminderDismissed] = useState(false)

  const latestMeasurement = getLatestMeasurement()

  const recentMeasurements = getRecentMeasurements(10)
  const recentAdditions = getRecentAdditions(10)

  // Combine and sort measurements and chemical additions by date
  const unifiedTimeline = useMemo(() => {
    const combined = [
      ...recentMeasurements.map(m => ({ 
        ...m, 
        type: 'measurement', 
        sortDate: new Date(m.measured_at).getTime() 
      })),
      ...recentAdditions.map(a => ({ 
        ...a, 
        type: 'addition', 
        sortDate: new Date(a.added_at).getTime() 
      }))
    ]
    
    return combined
      .sort((a, b) => b.sortDate - a.sortDate) // Most recent first
      .slice(0, 5) // Take only the 5 most recent items
  }, [recentMeasurements, recentAdditions])

  // Build at-a-glance parameter cards from the two most recent measurements
  const parameterCards = useMemo(() => {
    if (!selectedPool || recentMeasurements.length === 0) return []

    const latest = recentMeasurements[0]
    const previous = recentMeasurements.length > 1 ? recentMeasurements[1] : null
    const summaries = buildParameterSummaries(latest, previous)

    // Calculate target ranges for status determination
    let targetRanges = null
    try {
      targetRanges = calculateTargetRanges(selectedPool, latest)
    } catch {
      // Pool config may be incomplete (e.g. volume missing); skip status computation
      return summaries.map(s => ({ ...s, statusLabel: null, statusColor: null }))
    }

    return summaries.map(summary => {
      const range = targetRanges[summary.key]
      if (!range || summary.value === null) {
        return { ...summary, statusLabel: null, statusColor: null }
      }
      const status = getParameterStatus(summary.value, range)
      return {
        ...summary,
        statusLabel: getStatusText(status),
        statusColor: getStatusColor(status)
      }
    })
  }, [selectedPool, recentMeasurements])

  const handlePoolSelect = (pool) => {
    setSelectedPool(pool)
  }


  const handleEditMeasurement = (measurement) => {
    setEditingMeasurement(measurement)
    setShowMeasurementForm(true)
  }

  const handleDeleteMeasurement = async (measurementId) => {
    if (window.confirm('Are you sure you want to delete this measurement?')) {
      await deleteMeasurement(measurementId)
    }
  }

  const handleSaveMeasurement = async (measurementData) => {
    if (editingMeasurement) {
      return await updateMeasurement(editingMeasurement.id, measurementData)
    } else {
      return await createMeasurement(measurementData)
    }
  }

  const handleCancelMeasurement = () => {
    setShowMeasurementForm(false)
    setEditingMeasurement(null)
  }

  const handleEditAddition = (addition) => {
    // TODO: Implement edit addition functionality
    console.log('Edit addition:', addition)
  }

  const handleDeleteAddition = async (additionId) => {
    if (window.confirm('Are you sure you want to delete this chemical addition?')) {
      await deleteChemicalAddition(additionId)
    }
  }

  if (pools.length === 0) {
    return (
      <Layout>
        <Box sx={{ textAlign: 'center', py: { xs: 6, md: 12 } }}>
          <Typography variant={isMobile ? 'h4' : 'h3'} component="h2" gutterBottom color="primary">
            Welcome to TFPLog!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Start by creating your first pool.
          </Typography>
          <PoolList selectedPool={selectedPool} onPoolSelect={handlePoolSelect} />
        </Box>
      </Layout>
    )
  }

  return (
    <Layout>
      <Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            mb: 3
          }}
        >
          {/* Header with dual action buttons */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: { xs: 2, sm: 0 }
            }}
          >
            <Typography variant={isMobile ? 'h5' : 'h4'} component="h1">
              Dashboard
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="outlined"
                startIcon={<ScienceIcon />}
                component={Link}
                to="/add-chemicals"
                size={isMobile ? 'large' : 'medium'}
                sx={{ 
                  borderRadius: 2,
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  borderColor: 'secondary.main',
                  color: 'secondary.main',
                  '&:hover': {
                    borderColor: 'secondary.dark',
                    backgroundColor: 'secondary.light'
                  }
                }}
              >
                Add Chemicals
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                component={Link}
                to="/add-measurement"
                size={isMobile ? 'large' : 'medium'}
                sx={{ 
                  borderRadius: 2,
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                  boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
                }}
              >
                Add Measurement
              </Button>
            </Stack>
          </Box>

          {/* Pool selector */}
          <PoolSelector
            selectedPool={selectedPool}
            onPoolChange={handlePoolSelect}
            label="Select Pool"
            showAlert={false}
          />
        </Box>

        {/* Test Reminder Banner */}
        {selectedPool && (
          <TestReminder
            measuredAt={latestMeasurement?.measured_at}
            dismissed={reminderDismissed}
            onDismiss={() => setReminderDismissed(true)}
          />
        )}

        {/* At-a-glance Parameter Cards */}
        {selectedPool && parameterCards.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              Pool Health
            </Typography>
            <Grid container spacing={1.5}>
              {parameterCards.map(card => (
                <Grid key={card.key} size={{ xs: 6, md: 2.4 }}>
                  <ParameterCard
                    parameterKey={card.key}
                    label={card.label}
                    shortLabel={card.shortLabel}
                    unit={card.unit}
                    color={card.color}
                    value={card.value}
                    trend={card.trend}
                    statusLabel={card.statusLabel}
                    statusColor={card.statusColor}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Recent Activity Timeline */}
        {selectedPool && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              Recent Activity for {selectedPool.name}
            </Typography>
            {unifiedTimeline.length > 0 ? (
              <UnifiedTimeline
                items={unifiedTimeline}
                onEditMeasurement={handleEditMeasurement}
                onDeleteMeasurement={handleDeleteMeasurement}
                onEditAddition={handleEditAddition}
                onDeleteAddition={handleDeleteAddition}
                showActions={true}
              />
            ) : (
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <ScienceIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    No activity yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Start by adding your first measurement to track your pool chemistry.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    component={Link}
                    to="/add-measurement"
                    sx={{ 
                      borderRadius: 2,
                      py: 1.5,
                      textTransform: 'none',
                      fontSize: '1rem',
                    }}
                  >
                    Add First Measurement
                  </Button>
                </CardContent>
              </Card>
            )}
          </Box>
        )}

        {/* Measurement Form Modal */}
        {showMeasurementForm && (
          <Box sx={{ mt: 3 }}>
            <MeasurementForm
              measurement={editingMeasurement}
              onSave={handleSaveMeasurement}
              onCancel={handleCancelMeasurement}
            />
          </Box>
        )}
      </Box>
    </Layout>
  )
}

export default Dashboard